import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAdminSettings, updateAdminSettings, type AdminBranding } from '../../api/admin';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Tabs } from '../../components/Tabs';
import { PageHeader } from '../../components/PageHeader';
import { UploadField } from '../../components/UploadField';
import { uploadAsset } from '../../api/uploads';

/** Platform settings — 6 tabs (General, Email, SMS, Branding, Security, Data retention). */
export function PlatformSettings() {
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Settings"
        subtitle="Brand, contact info, billing defaults, and data retention."
      />

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'general', label: 'General', content: <GeneralTab /> },
            { id: 'email', label: 'Email', content: <EmailTab /> },
            { id: 'sms', label: 'SMS', content: <SmsTab /> },
            { id: 'branding', label: 'Branding', content: <BrandingTab /> },
            { id: 'security', label: 'Security', content: <SecurityTab /> },
            { id: 'retention', label: 'Data retention', content: <RetentionTab /> },
          ]}
        />
      </Card>
    </div>
  );
}

function GeneralTab() {
  const { data: settings } = useAdminSettings();
  const qc = useQueryClient();
  const [brand, setBrand] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [terms, setTerms] = useState('');
  const [privacy, setPrivacy] = useState('');
  const [currency, setCurrency] = useState('BDT');
  const [timezone, setTimezone] = useState('Asia/Dhaka');
  const [allowReseller, setAllowReseller] = useState(true);
  const [allowDirect, setAllowDirect] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setBrand(settings.branding.brandName);
    setSupportEmail(settings.branding.supportEmail);
    setSupportPhone(settings.branding.supportPhone ?? '');
    setTerms(settings.termsUrl ?? '');
    setPrivacy(settings.privacyUrl ?? '');
    setCurrency(settings.defaultCurrency);
    setTimezone(settings.defaultTimezone);
    setAllowReseller(settings.allowResellerSignup);
    setAllowDirect(settings.allowDirectSignup);
  }, [settings]);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      await updateAdminSettings({
        defaultCurrency: currency,
        defaultTimezone: timezone,
        termsUrl: terms.trim() || null,
        privacyUrl: privacy.trim() || null,
        allowResellerSignup: allowReseller,
        allowDirectSignup: allowDirect,
        branding: { brandName: brand.trim(), supportEmail: supportEmail.trim(), supportPhone: supportPhone.trim() || null },
      });
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  const selectCls =
    'block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700';

  if (!settings) {
    return <div className="px-1 py-8 space-y-4" aria-busy="true"><div className="skeleton h-8 w-64 max-w-full rounded" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>;
  }

  return (
    <div className="space-y-5">
      <Input label="Brand name" value={brand} onChange={(e) => setBrand(e.target.value)} />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Default currency</label>
          <select className={selectCls} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {settings.activeCurrencies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {/* This list used to be a hardcoded BDT/USD/GBP/EUR, so an admin could
              pick a currency the platform cannot bill, invoice or settle in.
              It is now the union of supported currencies over every enabled
              gateway (#36), so it grows by itself the day Stripe is turned on. */}
          <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
            {settings.activeCurrencies.length === 1
              ? `Only ${settings.activeCurrencies[0]} is available — it is the one currency the enabled payment gateways can settle. Enable another gateway on /admin/gateways to add more.`
              : 'Limited to the currencies the enabled payment gateways can settle.'}
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Default timezone</label>
          <select className={selectCls} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            <option value="Asia/Dhaka">Asia/Dhaka</option>
            <option value="UTC">UTC</option>
            <option value="Asia/Kolkata">Asia/Kolkata</option>
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input label="Support email" type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} />
        <Input label="Support phone" value={supportPhone} onChange={(e) => setSupportPhone(e.target.value)} />
      </div>
      <Input label="Terms URL" type="url" value={terms} onChange={(e) => setTerms(e.target.value)} />
      <Input label="Privacy URL" type="url" value={privacy} onChange={(e) => setPrivacy(e.target.value)} />

      <div className="space-y-2 pt-3 border-t border-zinc-200 dark:border-zinc-700">
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={allowReseller}
            onChange={(e) => setAllowReseller(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Allow public reseller application form
        </label>
        <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={allowDirect}
            onChange={(e) => setAllowDirect(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Allow direct (no-reseller) signups
        </label>
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function EmailTab() {
  const { data: settings } = useAdminSettings();
  const qc = useQueryClient();
  const [fromName, setFromName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    const e = settings.email;
    setFromName(e.fromName);
    setFromAddress(e.fromAddress);
    setReplyTo(e.replyTo ?? '');
    setHost(e.smtp.host ?? '');
    setPort(String(e.smtp.port));
    setSecure(e.smtp.secure);
    setUsername(e.smtp.username ?? '');
  }, [settings]);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    try {
      await updateAdminSettings({
        email: {
          fromName: fromName.trim(),
          fromAddress: fromAddress.trim(),
          replyTo: replyTo.trim() || null,
          smtp: {
            host: host.trim() || null,
            port: Number(port) || 587,
            secure,
            username: username.trim() || null,
            // Only send the password when the admin typed a new one (write-only; sealed server-side).
            ...(password ? { password } : {}),
          },
        },
      });
      setPassword('');
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  if (!settings) {
    return <div className="px-1 py-8 space-y-4" aria-busy="true"><div className="skeleton h-8 w-64 max-w-full rounded" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /><div className="skeleton h-14 rounded-lg" /></div>;
  }
  const passwordSet = settings.email.smtp.passwordSet;

  const active = settings.email.active;

  return (
    <div className="space-y-5">
      {/* What is ACTUALLY sending. Asked directly on 2026-07-30 — and the answer
          on prod is "not this tab": Mailgun env config wins over these fields, so
          they save but stay dormant. An admin editing SMTP here deserves to know
          that before wondering why nothing changed. */}
      <Banner
        tone={active.driver === 'log' ? 'warning' : 'info'}
        title={`Sending via: ${active.source}`}
      >
        {active.driver === 'mailgun' ? (
          <>
            Mail is delivered through Mailgun, configured in the server environment, which takes
            precedence over the SMTP transport below. The SMTP fields still save, but they will not
            be used until the Mailgun configuration is removed
            {active.fromAddress ? <> — and the live From address is <strong>{active.fromAddress}</strong>, not the one set here</> : null}.
          </>
        ) : active.driver === 'log' ? (
          <>No mail is leaving the server. Configure the SMTP transport below, or set Mailgun in the server environment.</>
        ) : (
          <>These SMTP settings are the live transport.</>
        )}
      </Banner>

      <Banner tone="info">SMTP credentials are encrypted at rest and never displayed.</Banner>

      <Input label="From name" value={fromName} onChange={(e) => setFromName(e.target.value)} />
      <Input label="From address" type="email" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} />
      <Input label="Reply-to address" type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">SMTP transport</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Host" value={host} onChange={(e) => setHost(e.target.value)} placeholder="smtp.example.com" />
          <Input label="Port" value={port} onChange={(e) => setPort(e.target.value)} />
          <Input label="Username" value={username} onChange={(e) => setUsername(e.target.value)} />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={passwordSet ? '•••••••• (set — type to replace)' : 'Not set'}
          />
        </div>
        <label className="mt-3 flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
          <input
            type="checkbox"
            checked={secure}
            onChange={(e) => setSecure(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
          />
          Use implicit TLS (port 465)
        </label>
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-between items-center gap-2">
        <Button variant="secondary" disabled title="Test send coming soon">Send test email</Button>
        <div className="flex items-center gap-3">
          {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
          <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * SMS gateway — READ-ONLY status.
 *
 * This tab used to render a provider dropdown (SSL Wireless / BulkSMSBD /
 * Twilio), a sender ID, an API-key box and Save / Send-test buttons. None of it
 * was wired: no handlers, no endpoint, and not one of the three listed providers
 * is the one the platform uses. It read as configuration and configured nothing.
 *
 * The real gateway is BoomCast, credentialed from the server environment
 * (BOOMCAST_*), which is where it belongs — an API password should not live in a
 * settings blob. So this now reports the truth instead of inventing a form.
 */
function SmsTab() {
  const { data: settings } = useAdminSettings();
  if (!settings) {
    return <div className="px-1 py-8 space-y-4" aria-busy="true"><div className="skeleton h-8 w-64 max-w-full rounded" /><div className="skeleton h-14 rounded-lg" /></div>;
  }
  const live = settings.sms.driver === 'boomcast';

  return (
    <div className="space-y-5">
      <Banner tone={live ? 'info' : 'warning'} title={live ? 'SMS gateway: BoomCast (live)' : 'SMS gateway: not configured'}>
        {live ? (
          <>Transactional SMS (OTP, dunning, broadcasts) is delivered through BoomCast.</>
        ) : (
          <>
            No SMS gateway credentials are present, so SMS is written to the application log instead of
            being delivered. Anything that offers SMS as a channel will fall back to email.
          </>
        )}
      </Banner>

      <dl className="rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700 text-sm">
        <StatusRow label="Provider" value="BoomCast" />
        <StatusRow label="Status" value={live ? 'Configured — sending' : 'Not configured — logging only'} />
        <StatusRow label="Sender ID (masking)" value={settings.sms.senderId ?? '—'} />
      </dl>

      <p className="text-xs text-zinc-500 dark:text-zinc-400">
        Credentials are set on the server as <span className="mono">BOOMCAST_USERNAME</span> /{' '}
        <span className="mono">BOOMCAST_PASSWORD</span> (plus optional{' '}
        <span className="mono">BOOMCAST_MASKING</span>) and deliberately are not editable here — an
        API password does not belong in the settings record.
      </p>
    </div>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
      <dt className="text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="text-zinc-950 dark:text-zinc-50 font-medium text-right">{value}</dd>
    </div>
  );
}

/** Platform-brand defaults (the brand every reseller white-labels over). Wired to
 *  GET/PATCH /admin/settings.branding + POST /uploads/asset (#54). */
function BrandingTab() {
  const qc = useQueryClient();
  const { data: settings } = useAdminSettings();
  const [draft, setDraft] = useState<AdminBranding | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings) setDraft(settings.branding);
  }, [settings]);

  if (!draft) {
    return <div className="py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">Loading branding…</div>;
  }

  const set = <K extends keyof AdminBranding>(key: K, value: AdminBranding[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  async function save() {
    if (!draft) return;
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await updateAdminSettings({ branding: draft });
      await qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch {
      setError('Could not save branding. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <Input label="Brand name" value={draft.brandName} onChange={(e) => set('brandName', e.target.value)} />

      <div className="grid sm:grid-cols-3 gap-4">
        <UploadField label="Logo (light)" hint="PNG/SVG · ≤ 2 MB" value={draft.logoLightUrl} onUpload={async (f) => set('logoLightUrl', (await uploadAsset(f)).url)} />
        <UploadField label="Logo (dark)" hint="PNG/SVG · ≤ 2 MB" value={draft.logoDarkUrl} onUpload={async (f) => set('logoDarkUrl', (await uploadAsset(f)).url)} />
        <UploadField label="Favicon" hint="ICO/PNG · ≤ 2 MB" value={draft.faviconUrl} onUpload={async (f) => set('faviconUrl', (await uploadAsset(f)).url)} />
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <ColorRow label="Primary" hex={draft.primaryColor} onChange={(v) => set('primaryColor', v)} />
        <ColorRow label="Brand accent" hex={draft.accentColor} onChange={(v) => set('accentColor', v)} />
        <ColorRow label="Surface" hex={draft.surfaceColor} onChange={(v) => set('surfaceColor', v)} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Input label="Support email" value={draft.supportEmail} onChange={(e) => set('supportEmail', e.target.value)} />
        <Input label="Support phone" value={draft.supportPhone ?? ''} onChange={(e) => set('supportPhone', e.target.value || null)} />
      </div>

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {error && <span className="text-sm text-rose-600 dark:text-rose-400">{error}</span>}
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

function ColorRow({ label, hex, onChange }: { label: string; hex: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">{label}</label>
      <div className="flex items-center gap-2 p-2 rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900">
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="w-6 h-6 rounded border border-zinc-200 dark:border-zinc-700 bg-transparent cursor-pointer"
          aria-label={`${label} color`}
        />
        <input value={hex} onChange={(e) => onChange(e.target.value)} className="flex-1 mono text-xs bg-transparent focus:outline-none" />
      </div>
    </div>
  );
}

function SecurityTab() {
  const qc = useQueryClient();
  const { data: settings } = useAdminSettings();
  const [maxSessions, setMaxSessions] = useState('5');
  const [otpRequired, setOtpRequired] = useState(true);
  const [otpDelivery, setOtpDelivery] = useState<'sms' | 'email' | 'both'>('both');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setMaxSessions(String(settings.maxActiveSessionsDefault));
    setOtpRequired(settings.signupOtpRequired);
  }, [settings]);

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await updateAdminSettings({ maxActiveSessionsDefault: Number(maxSessions), signupOtpRequired: otpRequired });
      void qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
          Maximum simultaneous device sessions per user
        </label>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={1}
            max={20}
            value={maxSessions}
            onChange={(e) => setMaxSessions(e.target.value)}
            className="w-28"
          />
          <span className="text-sm text-zinc-500 dark:text-zinc-400">devices (range 1–20)</span>
        </div>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          When a user signs in on more devices than the cap, the oldest session is automatically
          revoked. Per-user override available from <span className="mono text-[11px]">/admin/users/&lt;id&gt;</span> for VIP / shared accounts.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Signup OTP</label>
        <div className="space-y-2">
          <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
            <input
              type="checkbox"
              checked={otpRequired}
              onChange={(e) => setOtpRequired(e.target.checked)}
              className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
            />
            Require 4-digit OTP at signup (verifies email + phone)
          </label>
          {otpRequired && (
            <div className="ml-7 space-y-2">
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Delivery channel</div>
              <div className="flex gap-3">
                {(['sms', 'email', 'both'] as const).map((d) => (
                  <label key={d} className="flex items-center gap-1.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                    <input
                      type="radio"
                      name="otp-delivery"
                      checked={otpDelivery === d}
                      onChange={() => setOtpDelivery(d)}
                      className="text-brand focus:ring-brand"
                    />
                    <span className="capitalize">{d === 'both' ? 'SMS + Email' : d}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Removed 2026-08-01: a "Two-factor auth" checkbox group, a password-policy
          block (min length / max age / complexity / HIBP) and an admin IP
          allowlist. All four were uncontrolled inputs with no handler and no
          endpoint — they showed ticks and a plausible CIDR, and enforced nothing.
          Stating a security control is on when it is not is worse than not
          offering it. Tracked in docs/DEFERRED_BACKLOG.md; 2FA itself is being
          built as issue #24 and will bring its own controls. */}

      <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button onClick={save} loading={busy} disabled={busy}>Save changes</Button>
      </div>
    </div>
  );
}

/**
 * Data retention — honest placeholder.
 *
 * This tab used to present five editable retention periods (audit log, webhook
 * deliveries, notifications, soft-deleted records, canceled-subscription data)
 * with a Save button. Every field was an uncontrolled input, the button had no
 * handler, no endpoint existed, and — the part that matters — no job anywhere
 * deletes on a schedule. It told an operator that "canceled subscription data is
 * erased after 30 days" when nothing was ever erased.
 *
 * That is a compliance-grade claim, so it is not something to leave sitting
 * there looking configured. The controls are gone until the pruning jobs exist.
 */
function RetentionTab() {
  return (
    <div className="space-y-5">
      <Banner tone="warning" title="Retention is not enforced yet">
        No scheduled job prunes audit logs, webhook deliveries, notifications, soft-deleted rows or
        canceled-subscription data — everything is kept indefinitely. This tab previously offered
        editable retention periods and a Save button; they were not wired to anything, so they have
        been removed rather than left implying data was being deleted on a schedule.
      </Banner>
      <p className="text-sm text-zinc-600 dark:text-zinc-300">
        The controls return with the pruning jobs. Tracked in{' '}
        <span className="mono text-xs">docs/DEFERRED_BACKLOG.md</span>.
      </p>
    </div>
  );
}
